import React from 'react';
import { StageSection, Prose, KeyPoints } from '../../ContentPrimitives';
import { StageBlock, content, question } from '../../types';

export const meta = {
    title: 'Script: Programmable Spending',
    tagline: 'A deliberately small language, on purpose.',
};

// This stage expects 2 quiz questions configured in the admin panel, in this order:
//   Q0 - why Script is deliberately not Turing-complete
//   Q1 - what kinds of spending conditions Script can express beyond a single key
export const blocks: StageBlock[] = [
    content('intro', () => (
        <StageSection gradientBorder eyebrow="Stage 5 of 8" title="An output is locked by a tiny program, not just an address">
            <Prose>
                Every output is locked by a small program written in Bitcoin Script, a simple stack-based language.
                The most common pattern just says, in effect: "whoever can provide a valid signature matching this
                public key hash may spend this." Spending an output means providing an "unlocking" script (typically
                your signature) that, combined with the output's "locking" script, evaluates successfully.
            </Prose>
        </StageSection>
    )),

    content('not-turing-complete', () => (
        <StageSection title="Deliberately limited, unlike a general-purpose VM">
            <Prose>
                Bitcoin Script has no loops and a deliberately small set of operations: it is not Turing-complete.
                This is a design choice, not an oversight: it bounds exactly how much computation every validating
                node has to perform for any given script, and it shrinks the attack surface considerably. This is a
                sharp contrast with general-purpose smart contract platforms (like Ethereum's EVM), which can run
                arbitrary programs within a gas budget instead of a fixed, narrow rule set.
            </Prose>
        </StageSection>
    )),

    question(0),

    content('beyond-single-key', () => (
        <StageSection title="More than just 'pay to one key'">
            <Prose>
                Within those limits, Script can still express useful conditions beyond a single signature:
            </Prose>
            <KeyPoints items={[
                <><strong>Multisig</strong>: requiring signatures from several keys (e.g. 2-of-3) before funds can move.</>,
                <><strong>Timelocks</strong>: requiring a certain block height or time to pass before an output becomes spendable.</>,
                <><strong>Hashlocks</strong>: requiring the spender to reveal a value that hashes to a specific target.</>,
            ]} />
            <Prose>
                These patterns are the building blocks for things like escrow, refundable payments, and payment
                channels. The dedicated Script and Timelocks tracks on this platform go deep on how to construct
                and break them. Here, the goal is just recognizing that "locked to an address" is really shorthand
                for "locked by a program."
            </Prose>
        </StageSection>
    )),

    question(1),
];
